/**
 * vfxArchetypes.config — per-class/per-school VFX archetypes (Lane F,
 * vibe-pipeline-abilities, 2026-07-01 prototype). Ports the biome-archetype
 * pattern (`src/config/atmosphere/biomeArchetypes.ts` — a family layer stack
 * + family-typical defaults so a new biome is ~10 lines) onto abilities: a
 * family PALETTE + MOTION SIGNATURE so a new ability in an established
 * class/school reads as part of that family's kit instead of a one-off.
 *
 * Design doc: docs/architecture/ABILITY_VFX_ARCHETYPES.md — read it for the
 * inherit/override contract and the follow-up wiring plan. THIS FILE IS A
 * PROTOTYPE: it is intentionally decoupled from `src/game/effects/` (no
 * VFXPrimitive/EffectDef imports — this lane's task brief is tooling-only
 * and explicitly excludes touching rendering code) and is NOT wired into
 * any live effect-def or the VFXEffectRegistry. `archetypeImpactPrimitives`
 * returns a locally-typed primitive-suggestion shape whose fields
 * correspond 1:1 to the real `VFXPrimitive` fields (see the design doc's
 * field-mapping table) so a future ratified integration is a mechanical
 * "spread this into the real primitive" step, not a redesign.
 *
 * Color grounding: this file does NOT invent new colors — every palette
 * value below is lifted from an EXISTING registered effect in
 * `src/game/effects/config/abilities/MageVFX.ts` (frost_impact-style circle
 * colors, the fireball charge/impact palette, ARCANE_ORBIT's purple), so
 * the Mage prototype below matches what's already on screen today, not a
 * fresh design.
 */

/** Primitive kinds this prototype can suggest — the subset of `VFXPrimitive`
 *  types relevant to impact/cast motifs (see VFX_BUILDER_COOKBOOK's catalog
 *  for the full 11-primitive set). */
export type ArchetypePrimitiveKind = 'circle' | 'star' | 'shockwave' | 'cone' | 'rect' | 'arc';

export interface VfxArchetypePalette {
  /** The ability's authored palette tint when it has no ability-specific
   *  override — mirrors abilityPalette()'s `palette[0]`/`glowColor`. */
  primary: number;
  /** Secondary/tertiary tones for multi-primitive bursts (highlight + shard). */
  accents: number[];
  /** The color routed through `glow: true` primitives (the bloom-layer tint). */
  glow: number;
}

export interface VfxArchetypeMotion {
  /** Primary shape for the impact burst. */
  impactPrimitive: ArchetypePrimitiveKind;
  impactRadius: number;
  impactDurationMs: number;
  /** Tween ease name (ShapeSpawner/Phaser ease strings, e.g. 'Power2', 'Sine.easeIn'). */
  impactEase: string;
  /** Whether the family's impact motif routes through the bloom layer. */
  glow: boolean;
  /** Optional accompanying scatter (shards/embers/sparkle) around the impact. */
  scatter?: { count: number; radius: number; primitive: ArchetypePrimitiveKind };
  /** Optional cast-side charge/glow motif (played at the caster, pre-impact). */
  castPrimitive?: ArchetypePrimitiveKind;
  castDurationMs?: number;
}

export interface AbilityVfxArchetype {
  /** `${cls}.${school}` — the registry key, also the doc-facing id. */
  id: string;
  cls: string;
  school: string;
  palette: VfxArchetypePalette;
  motion: VfxArchetypeMotion;
}

// ─── Mage prototype — the 3 schools MageVFX.ts already has content for ────

const MAGE_FROST: AbilityVfxArchetype = {
  id: 'mage.frost',
  cls: 'mage',
  school: 'frost',
  // Lifted from MageVFX.ts's existing frost primitives (circle 0x7ec8e3 /
  // 0xd6eaf8 / 0xa8d8ea) and the frost_impact template in the VFX cookbook.
  palette: { primary: 0x7ec8e3, accents: [0xd6eaf8, 0xa8d8ea], glow: 0xaaddff },
  motion: {
    impactPrimitive: 'shockwave', // icy ring, not a soft burst — frost reads as a HARD surface effect
    impactRadius: 20,
    impactDurationMs: 350,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 6, primitive: 'rect', radius: 26 }, // ice-shard debris (cookbook's frost_impact template)
    castPrimitive: 'circle',
    castDurationMs: 250,
  },
};

const MAGE_FIRE: AbilityVfxArchetype = {
  id: 'mage.fire',
  cls: 'mage',
  school: 'fire',
  // Lifted from MageVFX.ts's fireball charge/impact circles (0xff7a18 core,
  // 0xffe08a highlight, 0xff4500 ember accent).
  palette: { primary: 0xff7a18, accents: [0xffe08a, 0xff4500], glow: 0xff4500 },
  motion: {
    impactPrimitive: 'circle', // soft, expanding bloom — fire reads as LIGHT, not a hard edge
    impactRadius: 18,
    impactDurationMs: 300,
    impactEase: 'Power2',
    glow: true, // fire ALWAYS glows — the family's one non-negotiable knob
    scatter: { count: 8, primitive: 'circle', radius: 24 }, // ember sparks
    castPrimitive: 'circle',
    castDurationMs: 400, // fire telegraphs longer — the charge-up read
  },
};

const MAGE_ARCANE: AbilityVfxArchetype = {
  id: 'mage.arcane',
  cls: 'mage',
  school: 'arcane',
  // Lifted from MageVFX.ts's ARCANE_ORBIT (0xAA00FF core, 0xCC66FF star accent).
  palette: { primary: 0xAA00FF, accents: [0xCC66FF], glow: 0xCC66FF },
  motion: {
    impactPrimitive: 'star', // arcane reads as SPARKLE/sharp geometry, not a round burst
    impactRadius: 12,
    impactDurationMs: 300,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 4, primitive: 'star', radius: 20 }, // mirrors ARCANE_ORBIT's existing scatter
    castPrimitive: 'circle', // the muzzle glow before the star burst lands
    castDurationMs: 250,
  },
};

// ─── Mage — physical school (wand/staff strikes; the last uncovered pair) ────
// F6 archetype-coverage close-out (2026-07-17): 10 mage abilities carry
// school 'physical' (autos, staff strikes). A mage's "physical" is still a
// spellcaster's — silver-white force with an arcane violet undertone, sharper
// and cleaner than warrior steel.

const MAGE_PHYSICAL: AbilityVfxArchetype = {
  id: 'mage.physical',
  cls: 'mage',
  school: 'physical',
  palette: { primary: 0xE8E8F8, accents: [0xBFA8FF], glow: 0xCCBBFF },
  motion: {
    impactPrimitive: 'circle', // clean force pop, not steel shatter
    impactRadius: 10,
    impactDurationMs: 240,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 3, primitive: 'circle', radius: 16 },
    castPrimitive: 'circle',
    castDurationMs: 220,
  },
};

// ─── Warrior — physical school (steel + impact, the one school Warriors have) ─

const WARRIOR_PHYSICAL: AbilityVfxArchetype = {
  id: 'warrior.physical',
  cls: 'warrior',
  school: 'physical',
  // Lifted from WarriorVFX.ts's sword_swing/execute_slash/ground_slam palette:
  // steel grey (0xAAAAAA/0xCCCCCC) + white spark (0xFFFFFF) + warm dust (0xBBAA88).
  palette: { primary: 0xAAAAAA, accents: [0xFFFFFF, 0xCCCCCC], glow: 0xDDDDCC },
  motion: {
    impactPrimitive: 'arc', // sweeping slash arc — warrior reads as a BLADE SWING
    impactRadius: 30,
    impactDurationMs: 250,
    impactEase: 'Power2',
    glow: false, // steel doesn't glow — it sparks (no bloom layer)
    scatter: { count: 8, primitive: 'circle', radius: 35 }, // steel spark particles
    castPrimitive: 'circle',
    castDurationMs: 200, // fast — warriors don't telegraph long
  },
};

// ─── Rogue — 3 schools (physical/shadow/nature per AbilityData distribution) ──

const ROGUE_PHYSICAL: AbilityVfxArchetype = {
  id: 'rogue.physical',
  cls: 'rogue',
  school: 'physical',
  // Lifted from AssassinVFX.ts's shadow_strike/eviscerate_slash: dark steel
  // (0x888888) + dark red finisher (0xCC0000) + blade streak (0xAAAAAA).
  palette: { primary: 0x888888, accents: [0xAAAAAA, 0xCC0000], glow: 0xAAAAAA },
  motion: {
    impactPrimitive: 'rect', // thin blade streak — rogue reads as a PRECISE CUT
    impactRadius: 25,
    impactDurationMs: 200,
    impactEase: 'Power2',
    glow: false,
    scatter: { count: 5, primitive: 'rect', radius: 22 }, // knife shards
    castPrimitive: 'circle',
    castDurationMs: 150, // fastest — rogues are instant
  },
};

const ROGUE_SHADOW: AbilityVfxArchetype = {
  id: 'rogue.shadow',
  cls: 'rogue',
  school: 'shadow',
  // Lifted from AssassinVFX.ts's dark_sight/smoke_bomb: dark purple
  // (0x6600CC/0x8800FF) + smoke grey (0x333333/0x555555).
  palette: { primary: 0x6600CC, accents: [0x8800FF, 0x330066], glow: 0x8800FF },
  motion: {
    impactPrimitive: 'circle', // soft dark bloom — shadow reads as SMOKE/DARKNESS
    impactRadius: 22,
    impactDurationMs: 350,
    impactEase: 'Sine.easeOut',
    glow: true, // shadow glows dark-purple through the bloom layer
    scatter: { count: 6, primitive: 'circle', radius: 28 }, // smoke wisps
    castPrimitive: 'circle',
    castDurationMs: 200,
  },
};

const ROGUE_NATURE: AbilityVfxArchetype = {
  id: 'rogue.nature',
  cls: 'rogue',
  school: 'nature',
  // Poison theme: toxic green (lifted from ABILITY_SCHOOL_COLORS.nature 0x44cc44
  // + AssassinVFX.ts's poison_splash accent). Rogue nature = poison, not druid nature.
  palette: { primary: 0x44cc44, accents: [0x88ff44, 0x226622], glow: 0x66ff66 },
  motion: {
    impactPrimitive: 'circle', // splashing poison — nature reads as a LIQUID SPLASH
    impactRadius: 16,
    impactDurationMs: 400,
    impactEase: 'Sine.easeOut',
    glow: true,
    scatter: { count: 7, primitive: 'circle', radius: 24 }, // poison droplets
    castPrimitive: 'circle',
    castDurationMs: 200,
  },
};

// ─── Druid — nature school (organic, earthen, lunar) ──────────────────────

const DRUID_NATURE: AbilityVfxArchetype = {
  id: 'druid.nature',
  cls: 'druid',
  school: 'nature',
  // Lifted from ABILITY_SCHOOL_COLORS.nature (0x44cc44) + RangerVFX.ts's
  // nature/poison primitives (0x00FF00, 0x66FF66, 0x006600). Druid nature
  // reads as ORGANIC GROWTH — leaves/moonlight/bloom — not rogue poison.
  palette: { primary: 0x44cc44, accents: [0x88ff44, 0x226622], glow: 0x66ff66 },
  motion: {
    impactPrimitive: 'circle', // soft bloom — nature reads as GROWTH/LIFE
    impactRadius: 20,
    impactDurationMs: 400,
    impactEase: 'Sine.easeOut',
    glow: true,
    scatter: { count: 6, primitive: 'circle', radius: 28 }, // leaf/spore motes
    castPrimitive: 'circle',
    castDurationMs: 300,
  },
};

// ─── Hunter — 4 schools (physical/nature/arcane/fire) ─────────────────────

const HUNTER_PHYSICAL: AbilityVfxArchetype = {
  id: 'hunter.physical',
  cls: 'hunter',
  school: 'physical',
  // Lifted from RangerVFX.ts's arrow/steel primitives (0xD4A574 leather,
  // 0xFFFFFF arrow tip, 0xCD853F wood/bow). Hunter physical reads as
  // PROJECTILE IMPACT — arrow strike, not a sword swing.
  palette: { primary: 0xD4A574, accents: [0xFFFFFF, 0xCD853F], glow: 0xDEB887 },
  motion: {
    impactPrimitive: 'rect', // thin arrow streak — hunter reads as a PROJECTILE HIT
    impactRadius: 22,
    impactDurationMs: 200,
    impactEase: 'Power2',
    glow: false, // arrows don't glow — they pierce
    scatter: { count: 5, primitive: 'rect', radius: 20 }, // splinter debris
    castPrimitive: 'circle',
    castDurationMs: 150, // fast — hunters are instant shooters
  },
};

const HUNTER_NATURE: AbilityVfxArchetype = {
  id: 'hunter.nature',
  cls: 'hunter',
  school: 'nature',
  // Lifted from RangerVFX.ts's poison_tip/serpent_sting (0x00FF00, 0x66FF66,
  // 0x006600). Hunter nature = poison/venom, like rogue nature but with a
  // projectile-sting feel.
  palette: { primary: 0x44cc44, accents: [0x88ff44, 0x226622], glow: 0x66ff66 },
  motion: {
    impactPrimitive: 'circle', // splashing venom — nature reads as a LIQUID STING
    impactRadius: 16,
    impactDurationMs: 350,
    impactEase: 'Sine.easeOut',
    glow: true,
    scatter: { count: 6, primitive: 'circle', radius: 22 }, // venom droplets
    castPrimitive: 'circle',
    castDurationMs: 200,
  },
};

const HUNTER_ARCANE: AbilityVfxArchetype = {
  id: 'hunter.arcane',
  cls: 'hunter',
  school: 'arcane',
  // Arcane shot — purple sparkle (ABILITY_SCHOOL_COLORS.arcane 0xcc66ff).
  // Hunter arcane reads as a MAGICAL ARROW — sharp star geometry, not a
  // soft mage bloom.
  palette: { primary: 0xcc66ff, accents: [0xAA00FF, 0xDDAAFF], glow: 0xCC66FF },
  motion: {
    impactPrimitive: 'star', // arcane reads as SPARKLE — sharp star hit
    impactRadius: 14,
    impactDurationMs: 250,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 5, primitive: 'star', radius: 20 }, // arcane sparkles
    castPrimitive: 'circle',
    castDurationMs: 200,
  },
};

const HUNTER_FIRE: AbilityVfxArchetype = {
  id: 'hunter.fire',
  cls: 'hunter',
  school: 'fire',
  // Explosive shot/wildfire bomb — fire bloom (0xff4500 from school colors,
  // 0xFF6600/0xFFAA00 from RangerVFX.ts's explosive_shot).
  palette: { primary: 0xff4500, accents: [0xFFAA00, 0xFF6600], glow: 0xFF6600 },
  motion: {
    impactPrimitive: 'circle', // soft bloom — fire reads as LIGHT/EXPLOSION
    impactRadius: 20,
    impactDurationMs: 300,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 8, primitive: 'circle', radius: 26 }, // ember sparks
    castPrimitive: 'circle',
    castDurationMs: 200,
  },
};

// ─── Paladin — holy + physical (golden divine, steel-with-light) ───────────

const PALADIN_HOLY: AbilityVfxArchetype = {
  id: 'paladin.holy',
  cls: 'paladin',
  school: 'holy',
  // Lifted from PaladinVFX.ts's holy_strike/judgment/divine_storm palette
  // (0xFFD700 gold, 0xFFFF00 bright holy, 0xFFFFFF divine white). Paladin
  // holy reads as DIVINE LIGHT — golden glow, always radiant.
  palette: { primary: 0xFFD700, accents: [0xFFFF00, 0xFFFFFF], glow: 0xFFFF88 },
  motion: {
    impactPrimitive: 'star', // holy reads as DIVINE GEOMETRY — radiant star
    impactRadius: 18,
    impactDurationMs: 350,
    impactEase: 'Sine.easeOut',
    glow: true, // holy ALWAYS glows — the family's one non-negotiable knob
    scatter: { count: 6, primitive: 'star', radius: 24 }, // holy sparkles
    castPrimitive: 'circle',
    castDurationMs: 300, // paladins telegraph with divine charge
  },
};

const PALADIN_PHYSICAL: AbilityVfxArchetype = {
  id: 'paladin.physical',
  cls: 'paladin',
  school: 'physical',
  // Paladin physical attacks still carry holy light — steel + gold.
  // Lifted from PaladinVFX.ts (0xFFD700, 0xAAAAAA steel, 0xFFFFFF).
  palette: { primary: 0xAAAAAA, accents: [0xFFD700, 0xFFFFFF], glow: 0xFFD700 },
  motion: {
    impactPrimitive: 'arc', // sweeping hammer/sword arc — paladin reads as a DIVINE SWING
    impactRadius: 28,
    impactDurationMs: 250,
    impactEase: 'Power2',
    glow: true, // paladin physical glows gold — distinct from warrior steel
    scatter: { count: 6, primitive: 'star', radius: 30 }, // golden sparks
    castPrimitive: 'circle',
    castDurationMs: 200,
  },
};

// ─── Priest — holy + shadow (gentle heal, dark mind) ──────────────────────

const PRIEST_HOLY: AbilityVfxArchetype = {
  id: 'priest.holy',
  cls: 'priest',
  school: 'holy',
  // Priest holy is SOFTER than paladin holy — gentle heal glow, not divine
  // judgment. Lifted from PaladinVFX.ts's holy_heal/flash_of_light (0xFFD700,
  // 0xFFFFCC, 0xFFFFFF) but with softer motion.
  palette: { primary: 0xFFD700, accents: [0xFFFFCC, 0xFFFFFF], glow: 0xFFFF88 },
  motion: {
    impactPrimitive: 'circle', // soft heal bloom — priest holy reads as HEALING LIGHT
    impactRadius: 20,
    impactDurationMs: 450, // slower — healing is gentle, not sharp
    impactEase: 'Sine.easeOut',
    glow: true,
    scatter: { count: 5, primitive: 'circle', radius: 26 }, // soft light motes
    castPrimitive: 'circle',
    castDurationMs: 350,
  },
};

const PRIEST_SHADOW: AbilityVfxArchetype = {
  id: 'priest.shadow',
  cls: 'priest',
  school: 'shadow',
  // Priest shadow = mind/spirit magic — dark purple, mental. Lifted from
  // AssassinVFX.ts's shadow primitives (0x6600CC, 0x8800FF, 0x330066) and
  // ABILITY_SCHOOL_COLORS.shadow (0x9933cc).
  palette: { primary: 0x9933cc, accents: [0x6600CC, 0x330066], glow: 0x8800FF },
  motion: {
    impactPrimitive: 'circle', // soft dark bloom — shadow reads as MIND/DARKNESS
    impactRadius: 22,
    impactDurationMs: 400,
    impactEase: 'Sine.easeOut',
    glow: true,
    scatter: { count: 7, primitive: 'circle', radius: 28 }, // shadow wisps
    castPrimitive: 'circle',
    castDurationMs: 250,
  },
};

// ─── Shaman — fire + frost + nature (elemental, primal) ───────────────────

const SHAMAN_FIRE: AbilityVfxArchetype = {
  id: 'shaman.fire',
  cls: 'shaman',
  school: 'fire',
  // Shaman fire = primal flame, elemental. Lifted from ABILITY_SCHOOL_COLORS
  // .fire (0xff4500) + MageVFX.ts fire palette (0xff7a18, 0xffe08a).
  palette: { primary: 0xff4500, accents: [0xFF7a18, 0xffe08a], glow: 0xff6600 },
  motion: {
    impactPrimitive: 'circle', // soft bloom — fire reads as LIGHT
    impactRadius: 18,
    impactDurationMs: 300,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 8, primitive: 'circle', radius: 24 }, // ember sparks
    castPrimitive: 'circle',
    castDurationMs: 300,
  },
};

const SHAMAN_FROST: AbilityVfxArchetype = {
  id: 'shaman.frost',
  cls: 'shaman',
  school: 'frost',
  // Shaman frost = ice, elemental. Lifted from MageVFX.ts frost palette
  // (0x7ec8e3, 0xd6eaf8, 0xa8d8ea) — same frost family identity.
  palette: { primary: 0x7ec8e3, accents: [0xd6eaf8, 0xa8d8ea], glow: 0xaaddff },
  motion: {
    impactPrimitive: 'shockwave', // icy ring — frost reads as a HARD surface effect
    impactRadius: 20,
    impactDurationMs: 350,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 6, primitive: 'rect', radius: 26 }, // ice-shard debris
    castPrimitive: 'circle',
    castDurationMs: 250,
  },
};

const SHAMAN_NATURE: AbilityVfxArchetype = {
  id: 'shaman.nature',
  cls: 'shaman',
  school: 'nature',
  // Shaman nature = lightning + earth, elemental. Distinct from druid
  // nature (organic) and rogue/hunter nature (poison). Shaman nature
  // reads as ELECTRIC STORM — sharp sparks, crackling.
  palette: { primary: 0x44cc44, accents: [0xFFFFFF, 0x00aa44], glow: 0x66ff66 },
  motion: {
    impactPrimitive: 'star', // lightning reads as SPARK — sharp geometry
    impactRadius: 16,
    impactDurationMs: 250,
    impactEase: 'Power2',
    glow: true,
    scatter: { count: 6, primitive: 'star', radius: 24 }, // electric sparks
    castPrimitive: 'circle',
    castDurationMs: 250,
  },
};

// ─── Warlock — fire + shadow (dark magic, corruption) ─────────────────────

const WARLOCK_FIRE: AbilityVfxArchetype = {
  id: 'warlock.fire',
  cls: 'warlock',
  school: 'fire',
  // Warlock fire = hellfire/corruption, darker than mage fire. Lifted
  // from ABILITY_SCHOOL_COLORS.fire (0xff4500) with darker accents
  // (0x8B2500 dark ember, 0xCC6633 burnt orange from RangerVFX.ts).
  palette: { primary: 0xff4500, accents: [0x8B2500, 0xCC6633], glow: 0xCC6633 },
  motion: {
    impactPrimitive: 'circle', // soft bloom — but darker, corruption fire
    impactRadius: 20,
    impactDurationMs: 400, // slower — warlock fire lingers/corrupts
    impactEase: 'Sine.easeOut',
    glow: true,
    scatter: { count: 8, primitive: 'circle', radius: 28 }, // dark embers
    castPrimitive: 'circle',
    castDurationMs: 350,
  },
};

const WARLOCK_SHADOW: AbilityVfxArchetype = {
  id: 'warlock.shadow',
  cls: 'warlock',
  school: 'shadow',
  // Warlock shadow = curse/corruption, darker than priest shadow.
  // Lifted from AssassinVFX.ts shadow palette (0x6600CC, 0x330066) with
  // darker accents — warlock shadow reads as CORRUPTION, not mind magic.
  palette: { primary: 0x6600CC, accents: [0x330066, 0x2A0044], glow: 0x440088 },
  motion: {
    impactPrimitive: 'circle', // dark cloud — warlock shadow reads as CORRUPTION
    impactRadius: 24,
    impactDurationMs: 450, // slow — curses linger
    impactEase: 'Sine.easeOut',
    glow: true,
    scatter: { count: 8, primitive: 'circle', radius: 30 }, // dark wisps
    castPrimitive: 'circle',
    castDurationMs: 300,
  },
};

export const ABILITY_VFX_ARCHETYPES: Record<string, AbilityVfxArchetype> = {
  'mage.frost': MAGE_FROST,
  'mage.fire': MAGE_FIRE,
  'mage.arcane': MAGE_ARCANE,
  'mage.physical': MAGE_PHYSICAL,
  'warrior.physical': WARRIOR_PHYSICAL,
  'rogue.physical': ROGUE_PHYSICAL,
  'rogue.shadow': ROGUE_SHADOW,
  'rogue.nature': ROGUE_NATURE,
  // ─── Lane L — AbilityVFXMap migration Phase 2 (14 new archetypes) ───
  'druid.nature': DRUID_NATURE,
  'hunter.physical': HUNTER_PHYSICAL,
  'hunter.nature': HUNTER_NATURE,
  'hunter.arcane': HUNTER_ARCANE,
  'hunter.fire': HUNTER_FIRE,
  'paladin.holy': PALADIN_HOLY,
  'paladin.physical': PALADIN_PHYSICAL,
  'priest.holy': PRIEST_HOLY,
  'priest.shadow': PRIEST_SHADOW,
  'shaman.fire': SHAMAN_FIRE,
  'shaman.frost': SHAMAN_FROST,
  'shaman.nature': SHAMAN_NATURE,
  'warlock.fire': WARLOCK_FIRE,
  'warlock.shadow': WARLOCK_SHADOW,
};

/** Resolve the archetype for a class+school, or null when this class/school
 *  combination has no archetype yet (only Mage's 3 schools are prototyped —
 *  see the design doc's rollout plan for the other 4 classes). */
export function resolveAbilityArchetype(cls: string, school: string): AbilityVfxArchetype | null {
  return ABILITY_VFX_ARCHETYPES[`${cls}.${school}`] ?? null;
}

/**
 * Feature flag for the archetype-derived cast VFX fallback. When enabled,
 * abilities that load TOML vfx but do not author a `cast_effect` get a
 * stable `arch_cast_<class>_<school>` id instead of `undefined`.
 */
export const ENABLE_ARCHETYPE_CAST_FALLBACK = true;

/** Canonical id for the archetype-derived cast effect of a class/school. */
export function archCastId(cls: string, school: string): string {
  return `arch_cast_${cls}_${school}`;
}

/** Canonical id for the archetype-derived impact effect of a class/school. */
export function archImpactId(cls: string, school: string): string {
  return `arch_impact_${cls}_${school}`;
}

/**
 * Build a ready-to-adapt list of primitive suggestions for a NEW ability in
 * this archetype's family — the "inherit" half of "inherit + override".
 * `overrides` replaces individual motion fields (the "override" half); the
 * palette always comes from the archetype (an author overrides COLOR the
 * same way MageVFX already does today — passing a canonical color via
 * `abilityVfxColor(id)` — archetypes don't change that contract, they just
 * give the FALLBACK a family identity instead of an arbitrary default).
 *
 * Returns a plain suggestion object, not a real `VFXPrimitive[]` — see the
 * design doc's field-mapping table for how each field corresponds to the
 * real primitive shape when this prototype graduates to production wiring.
 */
export function archetypeImpactSuggestion(
  archetype: AbilityVfxArchetype,
  overrides: Partial<VfxArchetypeMotion> = {},
): { primitive: ArchetypePrimitiveKind; radius: number; color: number; durationMs: number; ease: string; glow: boolean; scatter?: VfxArchetypeMotion['scatter'] } {
  const m = { ...archetype.motion, ...overrides };
  return {
    primitive: m.impactPrimitive,
    radius: m.impactRadius,
    color: archetype.palette.primary,
    durationMs: m.impactDurationMs,
    ease: m.impactEase,
    glow: m.glow,
    scatter: m.scatter,
  };
}
